import { Divider, Drawer, Input, Modal, Select, Tag } from "antd";
import TextArea from "antd/es/input/TextArea";
import { cdnURL } from "assets/Constant";
import { CardHeader } from "Components/Atoms/CardHeader";
import { Editor2 } from "Components/Editor";
import formatter from 'html-formatter';
import React, { useEffect } from "react";
import AceEditor from 'react-ace';
import { AiFillCloseCircle, AiFillEye } from "react-icons/ai";
import { BiCodeAlt } from "react-icons/bi";
import { MdPublish } from "react-icons/md";
import { toast } from "react-toastify";
import AdminServices from "services/api/AdminServices";
import AdminServicesPYV2 from "services/api/AdminServicesPYV2";

const convertBase64ImagesToCDN = async (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const images = doc.querySelectorAll("img");

    for (let img of images) {
        const src = img.getAttribute("src");

        if (src && src.startsWith("data:image")) {
            try {
                const res = await fetch(src);
                const blob = await res.blob();

                const ext = blob.type.split("/")[1];
                const file = new File(
                    [blob],
                    `kb-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`,
                    { type: blob.type }
                );

                const fileName = file.name;
                const folderName = "app-images/imgs/knowledge-base/articles";

                const data = new FormData();
                data.append("file", file);

                const uploadRes = await AdminServices.updateToS3({
                    fileName,
                    folderName,
                    data
                });

                if (uploadRes?.type === true && uploadRes?.data === "success") {
                    const newUrl = `${cdnURL}${folderName}/${fileName}`;
                    img.setAttribute("src", newUrl);
                }

            } catch (err) {
                console.log("Image conversion failed", err);
            }
        }
    }

    return doc.body.innerHTML;
};

export default function EditKBArticle({ selectedArticle = {}, allArticles = [], onSuccess = () => { }, onCancel = () => { } }) {

    const allAuthors = [
        { value: "Shubham Satyarth", label: "Shubham Satyarth" },
        { value: "Avinash Bhatt", label: "Avinash Bhatt" },
        { value: "Pranav Nanekar", label: "Pranav Nanekar" },
    ]

    const [title, setTitle] = React.useState('');
    const [author, setAuthor] = React.useState(null);
    const [keywords, setKeywords] = React.useState([]);
    const [content, setContent] = React.useState('');
    const [updatedContent, setUpdatedContent] = React.useState('');
    const [tag, setTag] = React.useState('');
    const [tree, setTree] = React.useState([]);
    const [sectionName, setSectionName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [previewHTML, setPreviewHTML] = React.useState(false);
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const [slugPart, setSlugPart] = React.useState('');
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = React.useState(true);
    const [slugError, setSlugError] = React.useState('');
    const [type, setType] = React.useState('article');

    const [originalArticle, setOriginalArticle] = React.useState(null);

    const [rawHTMLInput, setRawHTMLInput] = React.useState('');
    const [fixingHTML, setFixingHTML] = React.useState(false);

    const getFullSlug = () => {
        let base = tree.length
            ? tree.join('|').toLowerCase().replaceAll(' ', '-').replaceAll('|', '/')
            : '';
        return `/knowledge-base${base ? '/' + base : ''}/${slugPart}`;
    };

    const validateSlugPart = (val) => {
        if (!val) {
            setSlugError("Slug cannot be empty");
            return false;
        }
        const regex = /^[a-z0-9-]+$/;
        if (!regex.test(val)) {
            setSlugError("Only lowercase letters, numbers and - allowed");
            return false;
        }
        setSlugError('');
        return true;
    };

    const loadArticle = async () => {
        try {
            let res = await AdminServicesPYV2.getKBArticleBySlug({
                slug: selectedArticle?.Slug
            });
            if (res?.data) {
                const article = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
                setOriginalArticle(article);
                setTitle(article?.Title || '');
                setAuthor(article?.Author || null);
                setKeywords(article?.Keywords ? article.Keywords.split(',') : []);
                setContent(article?.Content || '');
                setUpdatedContent(article?.Content || '');
                setTree(article?.Tree ? article.Tree.split('|')?.filter(Boolean) : []);
                setDescription(article?.Description || '');
                setType(article?.Type || 'article');
                // extract slugPart
                const slug = article?.Slug || '';
                const lastPart = slug.split('/').filter(Boolean).pop() || '';
                setSlugPart(lastPart);
            }
        } catch {
            toast.error("Failed to load article");
        }
    };

    useEffect(() => {
        if (selectedArticle?.Slug) {
            loadArticle();
        }
    }, [selectedArticle]);

    const onChangeTitle = (e) => {
        setTitle(e.target.value);
    };

    const onChangeAuthor = (value) => setAuthor(value);

    const onChangeContent = (value) => {
        setContent(value);
    };

    const onChangeDescription = (value) => {
        setDescription(value);
    }

    const handleAddTag = (e) => {
        if (e.key === 'Enter' && (String(e.target.value).trim())) {
            setKeywords([...keywords, String(e.target.value).trim()])
            setTag("");
        }
    }

    const handleTagRemove = (e, i) => {
        let temp = [...keywords];
        temp.splice(i, 1);
        setKeywords(temp)
    }

    const handleValidations = () => {
        if (!title.trim()) return toast.warn("Enter valid title"), false;
        if (!author) return toast.warn("Choose a valid author!"), false;
        if (!description.trim()) return toast.warn("Enter valid description!"), false;
        if (!keywords.length) return toast.warn("Enter valid keywords"), false;
        if (!content) return toast.warn("Content is empty!"), false;
        if (!validateSlugPart(slugPart)) return false;

        const fullSlug = getFullSlug();

        let exists = allArticles.find(item =>
            item?.Slug === fullSlug &&
            item?._id !== selectedArticle?._id
        );

        if (exists) {
            toast.error("Slug already exists!");
            return false;
        }
        return true;
    }

    const handlePublish = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (handleValidations()) {
            const now = Date.now();
            const { _id, ...rest } = originalArticle || {};
            const data = {
                ...rest,
                Title: title.trim(),
                Description: description.trim(),
                Keywords: keywords.join(','),
                Author: author,
                Content: content,
                Tree: tree.join('|'),
                Slug: getFullSlug(),
                Type: type,
                UpdatedOn: now
            };
            let res = await AdminServicesPYV2.upsertKBArticle({
                data: { params: JSON.stringify(data) },
                article_id: selectedArticle?._id
            });
            if (res?.type) {
                toast.success("Article updated.");
                onSuccess();
            }
        }
        setLoading(false);
    }

    const handleFixHTML = async () => {
        if (!rawHTMLInput.trim()) {
            toast.error("Paste HTML first");
            return;
        }

        setFixingHTML(true);

        try {
            const fixed = await convertBase64ImagesToCDN(rawHTMLInput);

            // 🔥 update editor
            setUpdatedContent(fixed);
            setContent(fixed);

            toast.success("HTML fixed successfully");

        } catch (err) {
            toast.error("Failed to process HTML");
        }

        setFixingHTML(false);
    };

    React.useEffect(() => {
        if (openDrawer) {
            document.getElementById('blog-preview-drawer').innerHTML = content;
        }
    }, [openDrawer])

    return (
        <React.Fragment>
            <div style={{ background: "var(--white)", padding: "1.25rem", borderRadius: "8px", }} className="normalContainer">
                <div>
                    <div className='d-flex justify-content-between flex-column flex-md-row ' style={{ gap: "10px" }}>
                        <CardHeader heading={'Edit article'} />
                        <div style={{ display: "flex", gap: "20px" }}>
                            <button className='btnPrimary' onClick={() => {
                                setOpenDrawer(true)
                            }}>
                                <AiFillEye className='mr-2' /> Preview
                            </button>
                            <button className='btnBlack' onClick={() => setPreviewHTML(true)}>
                                <BiCodeAlt className='mr-2' />See HTML
                            </button>
                            <button onClick={() => onCancel()} className='btnRed'>
                                <AiFillCloseCircle className='mr-2' /> Cancel
                            </button>
                        </div>
                    </div>
                    <Divider style={{ margin: "1rem 0 0" }} />
                </div>
                <div>
                    <label>Title:</label>
                    <br />
                    <Input placeholder='Title' value={title} name='title' onChange={onChangeTitle} style={{ padding: "5px 10px" }} className='textMD' />
                </div>
                <div>
                    <label>Slug:</label>
                    <br />
                    <Input value={slugPart} placeholder="Enter Slug. It will used for article url." onChange={(e) => {
                        let v = e.target.value.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-')
                            .replace(/^-+/, '').replace(/-+$/, '');
                        setSlugPart(v);
                        setIsSlugManuallyEdited(true);
                        validateSlugPart(v);
                    }} />
                    {slugError && <div style={{ color: 'red', fontSize: '12px' }}>{slugError}</div>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px, 1fr))", gap: "1.25rem" }}>
                    <div>
                        <label>
                            Author:
                        </label>
                        <br />
                        <Select placeholder='Select a author' showSearch onChange={onChangeAuthor}
                            style={{ width: "280px" }} optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            } value={author} >
                            {allAuthors.map((item) => (
                                <Select.Option key={item.value} value={item.value}>
                                    {item.value}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label>
                            Keywords:
                        </label>
                        <br />
                        <div className="d-flex align-items-center flex-wrap rounded" style={{ width: "280px", gap: "5px" }}>
                            {keywords.length ? <div className="d-flex flex-wrap rounded" style={{ gap: "3px" }}>
                                {keywords.map((item, i) => (
                                    <span key={item + i} style={{ display: 'inline-block' }}>
                                        <Tag closable={true} onClose={(e) => handleTagRemove(e, i)} closeIcon={<AiFillCloseCircle color='var(--black)' />}>
                                            {item}
                                        </Tag>
                                    </span>
                                ))}
                            </div> : null}
                            <Input className='flex-grow-1 ' placeholder='Add keywords (Press Enter to add)'
                                onChange={(e) => setTag(e.target.value)} onKeyDown={handleAddTag} value={tag} />
                        </div>
                    </div>
                    <div>
                        <label>Type</label>
                        <br />
                        <Select value={type} onChange={setType} style={{ width: "280px" }}>
                            <Select.Option value="article">Article</Select.Option>
                            <Select.Option value="dynamic">Dynamic</Select.Option>
                        </Select>
                    </div>
                </div>
                <div>
                    <label>
                        Sections:
                    </label>
                    <br />
                    <div className="d-flex align-items-center flex-wrap rounded" style={{ gap: "5px" }}>
                        {tree.length ? <div className="d-flex flex-wrap rounded" style={{ gap: "3px" }}>
                            {tree.map((item, i) => (
                                <span key={item + i} style={{ display: 'inline-block' }}>
                                    <Tag closable={true} onClose={(e) => {
                                        let temp = [...tree];
                                        temp.splice(i, 1);
                                        setTree(temp)
                                    }} closeIcon={<AiFillCloseCircle color='var(--black)' />}>
                                        {item}
                                    </Tag>
                                </span>
                            ))}
                        </div> : null}
                        <Input className='flex-grow-1 ' placeholder='Add sections (Press Enter to add)'
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (String(e.target.value).trim())) {
                                    setTree([...tree, String(e.target.value).trim()])
                                    setSectionName('');
                                }
                            }} value={sectionName} onChange={(e) => setSectionName(e.target.value)} />
                    </div>
                    <div className="textXS w-500 mt-1">Note: Theses sections are basically categories at decreasing heirchical levels inside which the article will be shown in knowledge base sidebar.
                        Please ensure proper spelling & letter casing while adding sections as it will be shown without any change in sidebar in knowledge base.</div>
                </div>
                <div>
                    <label>Description:</label>
                    <br />
                    <TextArea className='' style={{ width: "100%", height: "100px", resize: "none", fontSize: "16px" }}
                        placeholder='Enter description' value={description} onChange={(e) => onChangeDescription(e.target.value)} />
                </div>
                {/* <div style={{ marginTop: "1rem" }}>
                    <label>Fix Base64 HTML (Temporary Tool):</label>

                    <TextArea
                        placeholder="Paste full HTML here..."
                        value={rawHTMLInput}
                        onChange={(e) => setRawHTMLInput(e.target.value)}
                        style={{ height: "120px", marginTop: "5px" }}
                    />

                    <button
                        className="btnBlack mt-2"
                        onClick={handleFixHTML}
                        disabled={fixingHTML}
                    >
                        {fixingHTML ? "Processing..." : "Convert Base64 → CDN Images"}
                    </button>
                </div> */}
                <div>
                    <label>Content:</label>
                    <Editor2 onChange={onChangeContent} content={updatedContent} />
                </div>
                <div>
                    <button className='btnPrimary' onClick={handlePublish} disabled={loading}>
                        <MdPublish className='mr-2' /> Publish
                    </button>
                </div>
                <Modal onOk={() => setPreviewHTML(false)} open={previewHTML} title='HTML Content' onCancel={() => setPreviewHTML(false)}
                    zIndex={1021}>
                    <AceEditor
                        placeholder="Write something"
                        mode="html"
                        fontSize={14}
                        onLoad={(editor) => {
                            editor.setReadOnly(true)
                        }}
                        width='100%'
                        showPrintMargin={true}
                        showGutter={true}
                        highlightActiveLine={true}
                        value={formatter.render(content)}
                        setOptions={{
                            enableBasicAutocompletion: false,
                            enableLiveAutocompletion: false,
                            enableSnippets: false,
                            showLineNumbers: true,
                            wrap: true,
                        }} />
                </Modal>
                <Drawer open={openDrawer} onClose={() => setOpenDrawer(false)} placement='right' title='Preview Blog'
                    styles={{ body: { color: "black", margin: "0px", padding: "0px", background: "white" } }}
                    closable={true}
                    closeIcon={<AiFillCloseCircle size='24px' />} width='100%' zIndex={1021}
                >
                    <div className='ql-editor'>
                        <h2>
                            {title}
                        </h2>
                        <div id='blog-preview-drawer'>
                        </div>
                    </div>
                </Drawer>
            </div>
        </React.Fragment>
    )
}